import { z } from "zod";
import { billingCycleSchema, paymentRequestSchema } from "./billing.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Super Admin money and operations (docs/specs/super-admin.md): payments, metrics, queues, plans, billing settings. */
const reason = z.string().trim().min(1).max(500);
const platform = { platformAdmin: true };
const tag = { tag: "platform-admin" };
// Payments (B-61's approvals) -----------------------------------------------------------------------------------
export const waitingPaymentSchema = z.object({
    tenantId: z.uuid(),
    businessName: z.string(),
    invoiceNumber: z.string(),
    waitingHours: z.number().int(),
    request: paymentRequestSchema,
});
export const waitingPaymentListSchema = z.object({ data: z.array(waitingPaymentSchema) });
export const listWaitingPayments = defineEndpoint({
    ...tag,
    name: "listWaitingPayments",
    method: "GET",
    path: "/v1/platform/payment-requests",
    access: platform,
    summary: "Payment requests waiting for approval, oldest first",
    status: 200,
    response: waitingPaymentListSchema,
});
export const approvePayment = defineEndpoint({
    ...tag,
    name: "approvePayment",
    method: "POST",
    path: "/v1/platform/payment-requests/{id}/approve",
    access: platform,
    summary: "The money arrived: record the payment and activate the plan",
    params: idParamsSchema,
    body: z.object({ note: z.string().trim().max(500).default("") }),
    status: 204,
    errors: ["CONFLICT"],
});
export const rejectPayment = defineEndpoint({
    ...tag,
    name: "rejectPayment",
    method: "POST",
    path: "/v1/platform/payment-requests/{id}/reject",
    access: platform,
    summary: "Not received or not right: tell the business why",
    params: idParamsSchema,
    body: z.object({ reason }),
    status: 204,
    errors: ["CONFLICT"],
});
// Metrics (SA-17) -----------------------------------------------------------------------------------------------
export const platformMetricsSchema = z.object({
    mrrPaisa: paisa(),
    activeTenants: z.number().int(),
    trialTenants: z.number().int(),
    restrictedTenants: z.number().int(),
    suspendedTenants: z.number().int(),
    months: z.array(z.object({
        month: z.string(),
        signups: z.number().int(),
        /** Businesses whose first payment was approved that month. */
        firstPayments: z.number().int(),
        /** Businesses suspended or closed that month. */
        churned: z.number().int(),
    })),
    /** Of all businesses that signed up, the share that ever paid (basis points). */
    trialToPaidBp: z.number().int(),
});
export const getPlatformMetrics = defineEndpoint({
    ...tag,
    name: "getPlatformMetrics",
    method: "GET",
    path: "/v1/platform/metrics",
    access: platform,
    summary: "MRR, businesses by status, signups, first payments and churn per month",
    status: 200,
    response: platformMetricsSchema,
});
// Operations (SA-18) --------------------------------------------------------------------------------------------
export const platformOperationsSchema = z.object({
    queues: z.array(z.object({
        name: z.string(),
        waiting: z.number().int(),
        active: z.number().int(),
        delayed: z.number().int(),
        failed: z.number().int(),
    })),
    failedJobs: z.array(z.object({
        queue: z.string(),
        id: z.string(),
        name: z.string(),
        tenantId: z.string().nullable(),
        failedReason: z.string(),
        attemptsMade: z.number().int(),
        finishedAt: z.iso.datetime().nullable(),
    })),
    syncReviews: z.array(z.object({ tenantId: z.uuid(), businessName: z.string(), open: z.number().int() })),
    health: z.object({ database: z.boolean(), redis: z.boolean() }),
    /** Not wired yet (B-68). */
    backups: z.object({ configured: z.boolean() }),
});
export const getPlatformOperations = defineEndpoint({
    ...tag,
    name: "getPlatformOperations",
    method: "GET",
    path: "/v1/platform/operations",
    access: platform,
    summary: "Queues, failed jobs, POS reviews and health",
    status: 200,
    response: platformOperationsSchema,
});
export const retryFailedJob = defineEndpoint({
    ...tag,
    name: "retryFailedJob",
    method: "POST",
    path: "/v1/platform/operations/jobs/{queue}/{jobId}/retry",
    access: platform,
    summary: "Run a failed job again",
    params: z.object({ queue: z.string().regex(/^[a-z][a-z0-9_-]{0,40}$/), jobId: z.string().min(1).max(200) }),
    body: z.object({ reason }),
    status: 204,
});
// Plans, prices, billing settings, coupons -----------------------------------------------------------------------
export const platformPlanSchema = z.object({
    id: z.uuid(),
    key: z.string(),
    nameEn: z.string(),
    nameBn: z.string(),
    status: z.enum(["active", "retired"]),
    isPublic: z.boolean(),
    isDefault: z.boolean(),
    trialDays: z.number().int().nullable(),
    values: z.record(z.string(), z.union([z.number().int(), z.boolean()]).nullable()),
    prices: z.object({ monthly: paisa().nullable(), yearly: paisa().nullable() }),
    // PL-03 (docs/specs/plan-catalogue.md), additive.
    sortOrder: z.number().int(),
    taglineEn: z.string(),
    taglineBn: z.string(),
    badgeEn: z.string(),
    badgeBn: z.string(),
    isHighlighted: z.boolean(),
    /** Per key, shown next to the value; only keys with a note. */
    notes: z.record(z.string(), z.object({ en: z.string(), bn: z.string() })),
    /** Businesses with this plan of their own. */
    businesses: z.number().int(),
    version: z.number().int(),
});
export const platformPlanListSchema = z.object({ data: z.array(platformPlanSchema) });
const planParams = z.object({ key: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/) });
export const listPlatformPlans = defineEndpoint({
    ...tag,
    name: "listPlatformPlans",
    method: "GET",
    path: "/v1/platform/plans",
    access: platform,
    summary: "Plans with every value and price",
    status: 200,
    response: platformPlanListSchema,
});
export const setPlanValue = defineEndpoint({
    ...tag,
    name: "setPlanValue",
    method: "POST",
    path: "/v1/platform/plans/{key}/values",
    access: platform,
    summary: "Change one value of a plan (applies to every business on it at once)",
    params: planParams,
    body: z.object({
        entitlement: z.string().min(1).max(60),
        value: z.union([z.number().int().min(0), z.boolean()]).nullable(),
        reason,
    }),
    status: 200,
    response: platformPlanListSchema,
});
export const setPlanPrice = defineEndpoint({
    ...tag,
    name: "setPlanPrice",
    method: "POST",
    path: "/v1/platform/plans/{key}/prices",
    access: platform,
    summary: "Change a plan's price for one cycle (new invoices only)",
    params: planParams,
    body: z.object({ cycle: billingCycleSchema, pricePaisa: paisa().positive(), reason }),
    status: 200,
    response: platformPlanListSchema,
});
export const billingSettingsSchema = z.object({
    bkashNumber: z.string(),
    bkashAccountType: z.enum(["personal", "agent", "merchant"]),
    sellerName: z.string(),
    sellerAddress: z.string(),
    sellerBin: z.string(),
    vatBp: z.number().int(),
    invoiceDueDays: z.number().int(),
    notifyEmail: z.string(),
    reminderDays: z.array(z.number().int()),
    graceDays: z.number().int(),
    restrictedDays: z.number().int(),
    retentionDays: z.number().int(),
    warnBeforeDays: z.number().int(),
    version: z.number().int(),
});
export const updateBillingSettingsSchema = z
    .object({
    bkashNumber: z.string().trim().max(32),
    bkashAccountType: z.enum(["personal", "agent", "merchant"]),
    sellerName: z.string().trim().min(1).max(200),
    sellerAddress: z.string().trim().max(500),
    sellerBin: z
        .string()
        .trim()
        .regex(/^(\d{13})?$/),
    vatBp: z.number().int().min(0).max(10_000),
    invoiceDueDays: z.number().int().min(0).max(90),
    notifyEmail: z.union([z.email(), z.literal("")]),
    reminderDays: z.array(z.number().int().min(1).max(60)).max(5),
    graceDays: z.number().int().min(0).max(90),
    restrictedDays: z.number().int().min(0).max(90),
    retentionDays: z.number().int().min(30).max(365),
    warnBeforeDays: z.number().int().min(0).max(14),
})
    .partial()
    .extend({ version: z.number().int(), reason });
export const getBillingSettings = defineEndpoint({
    ...tag,
    name: "getBillingSettings",
    method: "GET",
    path: "/v1/platform/billing-settings",
    access: platform,
    summary: "Where businesses pay, invoice seller details, VAT and the payment timeline",
    status: 200,
    response: billingSettingsSchema,
});
export const updateBillingSettings = defineEndpoint({
    ...tag,
    name: "updateBillingSettings",
    method: "PATCH",
    path: "/v1/platform/billing-settings",
    access: platform,
    summary: "Change billing settings (with version)",
    body: updateBillingSettingsSchema,
    status: 200,
    response: billingSettingsSchema,
    errors: ["CONFLICT"],
});
export const platformCouponSchema = z.object({
    id: z.uuid(),
    code: z.string(),
    percentBp: z.number().int().nullable(),
    amountPaisa: paisa().nullable(),
    validUntil: z.iso.datetime().nullable(),
    maxRedemptions: z.number().int().nullable(),
    redemptions: z.number().int(),
    active: z.boolean(),
});
export const platformCouponListSchema = z.object({ data: z.array(platformCouponSchema) });
export const listPlatformCoupons = defineEndpoint({
    ...tag,
    name: "listPlatformCoupons",
    method: "GET",
    path: "/v1/platform/coupons",
    access: platform,
    summary: "Subscription coupons",
    status: 200,
    response: platformCouponListSchema,
});
export const createPlatformCoupon = defineEndpoint({
    ...tag,
    name: "createPlatformCoupon",
    method: "POST",
    path: "/v1/platform/coupons",
    access: platform,
    summary: "A new subscription coupon (percent or amount)",
    body: z
        .object({
        code: z.string().trim().min(2).max(40),
        percentBp: z.number().int().min(1).max(10_000).optional(),
        amountPaisa: paisa().positive().optional(),
        validUntil: z.iso.datetime().optional(),
        maxRedemptions: z.number().int().min(1).optional(),
        reason,
    })
        .refine((c) => (c.percentBp === undefined) !== (c.amountPaisa === undefined), "Percent or amount, not both"),
    status: 201,
    response: platformCouponSchema,
    errors: ["CONFLICT"],
});
//# sourceMappingURL=platform-ops.js.map